/**
 * "Something left your basket while you were holding it."
 *
 * A catalogue push can drop a line at any moment — `setMenu` re-prices the cart
 * against the new document and removes what no longer exists (`store/kiosk.ts`).
 * The customer has to be told, because the alternative is a total that changed
 * for no stated reason, or a basket that is refused at the till.
 *
 * This lived inline in `menu.tsx` and nowhere else, which made the notice a
 * property of *the screen the customer happened to be on* rather than of the
 * event. A push during checkout or on the payment screen — exactly when a
 * silent change matters most, because the customer is looking at the amount
 * they are about to pay — said nothing at all. So it is a component, and the
 * three screens that can be open when a menu arrives all mount it.
 *
 * It is dismissed rather than timed out: `Notice` is for things a customer acts
 * on, and acknowledging that the basket changed is the action.
 */
import { Button, Notice } from '@/components/ui'
import { useT } from '@/i18n'
import { useKiosk } from '@/store/kiosk'

export function RemovedNotice() {
  const t = useT()
  const removed = useKiosk((s) => s.removed)
  const clearRemoved = useKiosk((s) => s.clearRemoved)

  if (removed.length === 0) return null
  return (
    <Notice
      tone="warn"
      action={
        <Button variant="ghost" onClick={clearRemoved}>
          {t('common.ok')}
        </Button>
      }
    >
      {t('cart.removedUnavailable')}
    </Notice>
  )
}
